import { Auth } from "aws-amplify";
import { STSClient, AssumeRoleCommand } from "@aws-sdk/client-sts";
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient, QueryCommand } from "@aws-sdk/lib-dynamodb";
import { isProd } from "src/util/is-prod";

interface InvoiceTrackerConfig {
  roleArn: string;
  tableName: string;
  region: string;
}

const BETA_CONFIG: InvoiceTrackerConfig = {
  roleArn: "arn:aws:iam::730335579107:role/FuseInvoiceTracker-PortalRead-beta",
  tableName: "FuseInvoiceTracker-beta",
  region: "us-east-1",
};

const PROD_CONFIG: InvoiceTrackerConfig = {
  roleArn: "arn:aws:iam::637423481244:role/FuseInvoiceTracker-PortalRead-prod",
  tableName: "FuseInvoiceTracker-prod",
  region: "us-east-1",
};

function getConfig(): InvoiceTrackerConfig {
  return isProd() ? PROD_CONFIG : BETA_CONFIG;
}

/**
 * Maps FACD serviceProvider to Centauri product_code for DDB PK construction.
 * Source: confluencear_dwh.ar_invoice_all.product_code column.
 */
const SERVICE_PROVIDER_TO_PRODUCT_CODE: Record<string, string> = {
  PRIME: "1100",
  VIDEO: "1415",
  PVGO: "1415",
};

/** Returns the DDB partition key: resellerId#productCode#channelCode, or null if serviceProvider is unknown. */
export function buildTrackerPK(
  resellerId: string,
  serviceProvider: string,
  channelCode?: string,
): string | null {
  const productCode = SERVICE_PROVIDER_TO_PRODUCT_CODE[serviceProvider];
  if (!productCode) {
    console.warn(
      `Unknown serviceProvider: ${serviceProvider} for reseller ${resellerId}`,
    );
    return null;
  }
  const channel = channelCode || "0000";
  return `${resellerId}#${productCode}#${channel}`;
}

export interface InvoiceRecord {
  resellerId: string;
  billingPeriod: string;
  productName: string;
  amount: number;
  invoiceCurrencyCode: string;
  batchSourceName: string;
  noteText?: string;
  glPostedDate?: string;
  lastUpdatedDate?: string;
  lastUpdatedBy?: string;
  functionalAmount: number;
  functionalCurrencyCode: string;
  exchangeRate: number;
}

let cachedClient: DynamoDBDocumentClient | null = null;
let cacheExpiry = 0;

/**
 * Assumes the cross-account PortalRead role and returns a DDB DocumentClient.
 * Caches credentials for 50 minutes (STS session lasts 1 hour).
 */
async function getInvoiceTrackerDocClient(): Promise<DynamoDBDocumentClient> {
  if (cachedClient && Date.now() < cacheExpiry) return cachedClient;

  const config = getConfig();
  const cognitoCreds = Auth.essentialCredentials(
    await Auth.currentCredentials(),
  );

  const sts = new STSClient({
    credentials: cognitoCreds,
    region: config.region,
  });
  const assumed = await sts.send(
    new AssumeRoleCommand({
      RoleArn: config.roleArn,
      RoleSessionName: "BartPortalInvoiceRead",
    }),
  );

  const creds = assumed.Credentials;
  if (!creds?.AccessKeyId || !creds?.SecretAccessKey || !creds?.SessionToken) {
    throw new Error("STS AssumeRole returned incomplete credentials");
  }

  const crossAccountCreds = {
    accessKeyId: creds.AccessKeyId,
    secretAccessKey: creds.SecretAccessKey,
    sessionToken: creds.SessionToken,
  };

  const ddb = new DynamoDBClient({
    credentials: crossAccountCreds,
    region: config.region,
  });
  cachedClient = DynamoDBDocumentClient.from(ddb);
  cacheExpiry = Date.now() + 50 * 60 * 1000;
  return cachedClient;
}

/**
 * Queries all invoices for a reseller by constructing the composite DDB partition key.
 * @param resellerId - The reseller identifier (e.g. "megacablemx")
 * @param productCode - Centauri product code (e.g. "1100")
 * @param channelCode - ARD channel code (e.g. "2770"), defaults to "0000"
 * @param billingPeriodFrom - Inclusive lower bound for SK range (YYYY-MM)
 * @param billingPeriodTo - Inclusive upper bound for SK range (YYYY-MM)
 */
export async function queryInvoices(
  resellerId: string,
  productCode: string,
  channelCode: string,
  billingPeriodFrom?: string,
  billingPeriodTo?: string,
): Promise<InvoiceRecord[]> {
  const partitionKey = `${resellerId}#${productCode}#${channelCode}`;
  const config = getConfig();
  const client = await getInvoiceTrackerDocClient();

  let keyCondition = "#pk = :pk";
  const exprValues: Record<string, string> = { ":pk": partitionKey };
  const exprNames: Record<string, string> = {
    "#pk": "resellerId#productCode#channelCode",
  };

  if (billingPeriodFrom && billingPeriodTo) {
    keyCondition += " AND #sk BETWEEN :skFrom AND :skTo";
    exprValues[":skFrom"] = billingPeriodFrom;
    exprValues[":skTo"] = billingPeriodTo + "~";
    exprNames["#sk"] = "billingPeriod#productName";
  } else if (billingPeriodFrom) {
    keyCondition += " AND #sk >= :skFrom";
    exprValues[":skFrom"] = billingPeriodFrom;
    exprNames["#sk"] = "billingPeriod#productName";
  } else if (billingPeriodTo) {
    keyCondition += " AND #sk <= :skTo";
    exprValues[":skTo"] = billingPeriodTo + "~";
    exprNames["#sk"] = "billingPeriod#productName";
  }

  const items: Record<string, unknown>[] = [];
  let exclusiveStartKey: Record<string, unknown> | undefined;

  do {
    const resp = await client.send(
      new QueryCommand({
        TableName: config.tableName,
        KeyConditionExpression: keyCondition,
        ExpressionAttributeValues: exprValues,
        ...(Object.keys(exprNames).length > 0 && {
          ExpressionAttributeNames: exprNames,
        }),
        ...(exclusiveStartKey && { ExclusiveStartKey: exclusiveStartKey }),
      }),
    );
    items.push(...(resp.Items ?? []));
    exclusiveStartKey = resp.LastEvaluatedKey;
  } while (exclusiveStartKey);

  return items.map((item) => {
    const sk = item["billingPeriod#productName"] as string;
    const hashIdx = sk.indexOf("#");
    const pk = item["resellerId#productCode#channelCode"] as string;
    const [resellerIdPart] = pk.split("#");
    return {
      resellerId: resellerIdPart,
      billingPeriod: sk.substring(0, hashIdx),
      productName: sk.substring(hashIdx + 1),
      amount: item.invoiceAmount as number,
      invoiceCurrencyCode: item.invoiceCurrencyCode as string,
      batchSourceName: item.batchSourceName as string,
      noteText: item.noteText as string | undefined,
      glPostedDate: item.glPostedDate as string | undefined,
      lastUpdatedDate: item.lastUpdatedDate as string | undefined,
      lastUpdatedBy: item.lastUpdatedBy as string | undefined,
      functionalAmount: item.functionalAmount as number,
      functionalCurrencyCode: item.functionalCurrencyCode as string,
      exchangeRate: item.exchangeRate as number,
    };
  });
}

/**
 * Queries invoices for multiple composite partition keys with concurrency limit.
 * Each key should be in format: resellerId#productCode#channelCode.
 * Accepts an optional boundary map (pk -> first IB period) to filter
 * tracker records to only those from the current config onwards.
 * Accepts reportMonth (YYYY-MM) as upper boundary for billing periods.
 * Returns a map of pk -> InvoiceRecord[] (or null if query failed).
 */
export async function queryInvoicesForResellers(
  partitionKeys: string[],
  boundaries?: Record<string, string>,
  reportMonth?: string,
): Promise<Record<string, InvoiceRecord[] | null>> {
  const BATCH_SIZE = 10;
  const results: [string, InvoiceRecord[] | null][] = [];

  for (let i = 0; i < partitionKeys.length; i += BATCH_SIZE) {
    const batch = partitionKeys.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.all(
      batch.map(async (pk) => {
        try {
          const [resellerId, productCode, channelCode] = pk.split("#");
          const from = boundaries?.[pk];
          const records = await queryInvoices(
            resellerId,
            productCode,
            channelCode,
            from,
            reportMonth,
          );
          return [pk, records] as [string, InvoiceRecord[] | null];
        } catch (err) {
          console.error(`InvoiceTracker query failed for ${pk}:`, err);
          return [pk, null] as [string, InvoiceRecord[] | null];
        }
      }),
    );
    results.push(...batchResults);
  }

  return Object.fromEntries(results);
}
